import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
  LayoutDashboard,
  Search,
  Plus,
  Trash2,
  Cloud,
  AlertCircle,
  FolderPlus,
  Star,
  Settings,
  Ban,
  X,
  ChevronRight,
  ChevronDown,
  Share2,
  HardDrive,
  Eye,
  EyeOff,
  Tags,
} from 'lucide-react';
import type { AssetMetadata, BlockedNasPath, FilterOptions } from '@linein/shared';
import { apiService } from './services/api';
import AssetCard from './components/AssetCard';
import UploadModal from './components/UploadModal';
import EditModal from './components/EditModal';
import ContextMenu, { ContextMenuState } from './components/ContextMenu';
import MarqueeSelection, { MarqueeBox } from './components/MarqueeSelection';
import Lightbox from './components/Lightbox';
import { FolderTreeItem, getFolderTree } from './components/FolderTree';
import UploadProgress, { UploadTask } from './components/UploadProgress';
import NasSyncPanel from './components/NasSyncPanel';

interface NasTreeNode {
  name: string;
  path: string;
  children: NasTreeNode[];
  directCount: number;  // assets directly in this folder
  totalCount: number;   // assets in this folder + all descendants
}

interface TagModalTarget {
  type: 'assets' | 'nas-folder';
  title: string;
  assetIds: string[];
}

interface TagDefinition {
  tagKey: string;
  category: string;
  name: string;
  usageCount?: number;
  createdAt?: string;
}

const App: React.FC = () => {
  const initialUrlSearch =
    typeof window !== 'undefined'
      ? (new URLSearchParams(window.location.search).get('q') || '')
      : '';

  const [assets, setAssets] = useState<AssetMetadata[]>([]);
  const [allAssets, setAllAssets] = useState<AssetMetadata[]>([]);
  const [nasTreeData, setNasTreeData] = useState<import('@linein/shared').ExplorerDirNode[]>([]);
  const [extraFolders, setExtraFolders] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedAsset, setSelectedAsset] = useState<AssetMetadata | null>(null);
  const [lightboxAsset, setLightboxAsset] = useState<AssetMetadata | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [cardScale, setCardScale] = useState(150);
  const [uploadTasks, setUploadTasks] = useState<UploadTask[]>([]);
  const [filters, setFilters] = useState<FilterOptions>({
    search: initialUrlSearch,
    starredOnly: false,
    sourceType: 'ALL',
    folder: '',
    tag: '',
  });
  const [searchInput, setSearchInput] = useState(initialUrlSearch);

  const [searchTagTab, setSearchTagTab] = useState('產品');
  const [contextMenu, setContextMenu] = useState<ContextMenuState>({
    x: 0,
    y: 0,
    visible: false,
    type: 'BACKGROUND',
  });
  const [marquee, setMarquee] = useState<MarqueeBox | null>(null);
  const [clipboardFolder, setClipboardFolder] = useState<{
    path: string;
    action: 'CUT' | 'COPY';
  } | null>(null);
  const [storageStatus, setStorageStatus] = useState({
    usedGB: 4.5,
    totalGB: 10,
    usagePercentage: 45,
  });
  const [activePanel, setActivePanel] = useState<'search' | 'nas' | 'settings' | null>('search');
  const [panelWidth, setPanelWidth] = useState(288);
  const [isResizingPanel, setIsResizingPanel] = useState(false);
  const [isBottomInfoCollapsed, setIsBottomInfoCollapsed] = useState(false);
  const [nasPathMode, setNasPathMode] = useState<'exact' | 'subtree'>('subtree');
  const [hiddenNasPaths, setHiddenNasPaths] = useState<Set<string>>(new Set());
  const [selectedNasPath, setSelectedNasPath] = useState<string>('');
  const [expandedNasPaths, setExpandedNasPaths] = useState<Set<string>>(new Set());
  const [nasTreeKeyword, setNasTreeKeyword] = useState('');
  const [blockedNasPaths, setBlockedNasPaths] = useState<BlockedNasPath[]>([]);
  const [busyPath, setBusyPath] = useState<string | null>(null);
  const [globalToast, setGlobalToast] = useState<string | null>(null);
  const [globalToastTone, setGlobalToastTone] = useState<'success' | 'error' | 'info'>('info');
  const [showShareModal, setShowShareModal] = useState(false);
  const [sharingHdLinks, setSharingHdLinks] = useState(false);
  const [tagDefinitions, setTagDefinitions] = useState<TagDefinition[]>([]);
  const [showTagModal, setShowTagModal] = useState(false);
  const [tagModalTarget, setTagModalTarget] = useState<TagModalTarget | null>(null);
  const [tagActionMode, setTagActionMode] = useState<'add' | 'remove'>('add');
  const [selectedTagKeys, setSelectedTagKeys] = useState<Set<string>>(new Set());
  const [newTagCategory, setNewTagCategory] = useState('產品');
  const [newTagName, setNewTagName] = useState('');
  const [tagSubmitting, setTagSubmitting] = useState(false);
  const [pendingDeepLinkPhotoId, setPendingDeepLinkPhotoId] = useState<string | null>(null);
  const [pendingScrollTop, setPendingScrollTop] = useState<number | null>(null);
  const toastTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isApplyingUrlRef = useRef(false);
  const latestAssetsRequestIdRef = useRef(0);
  const panelResizeStartXRef = useRef(0);
  const panelResizeStartWidthRef = useRef(288);

  const containerRef = useRef<HTMLDivElement>(null);

  const getAssetTagKeys = useCallback((asset: AssetMetadata): string[] => {
    const withTagKeys = asset as AssetMetadata & { tagKeys?: string[] };
    return withTagKeys.tagKeys || [];
  }, []);

  const getTagLabel = useCallback((definition: TagDefinition): string => {
    return `${definition.category} / ${definition.name}`;
  }, []);

  const getTagNameFromKey = useCallback((key: string): string => {
    const parts = key.split('|||');
    if (parts.length >= 3) return parts[2] || key;
    if (parts.length === 2) return parts[1] || key;
    return key;
  }, []);

  const updateUrlState = useCallback(
    (
      patch: { q?: string; scroll?: number | null; photo?: string | null },
      mode: 'replace' | 'push' = 'replace'
    ) => {
      if (typeof window === 'undefined') return;
      const params = new URLSearchParams(window.location.search);

      if (patch.q !== undefined) {
        const value = patch.q.trim();
        if (value) params.set('q', value);
        else params.delete('q');
      }

      if (patch.scroll !== undefined) {
        if (patch.scroll === null) {
          params.delete('scroll');
        } else {
          params.set('scroll', `${Math.max(0, Math.floor(patch.scroll))}`);
        }
      }

      if (patch.photo !== undefined) {
        if (patch.photo) params.set('photo', patch.photo);
        else params.delete('photo');
      }

      const query = params.toString();
      const nextUrl = `${window.location.pathname}${query ? `?${query}` : ''}${window.location.hash}`;
      isApplyingUrlRef.current = true;
      window.history[mode === 'push' ? 'pushState' : 'replaceState']({}, '', nextUrl);
      setTimeout(() => {
        isApplyingUrlRef.current = false;
      }, 0);
    },
    []
  );

  const applyUrlStateToUi = useCallback(() => {
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    const urlSearch = params.get('q') || '';
    const urlPhotoId = params.get('photo') || null;
    const urlScrollRaw = params.get('scroll');

    setSearchInput(urlSearch);
    setFilters((prev) => ({ ...prev, search: urlSearch }));
    setPendingDeepLinkPhotoId(urlPhotoId);

    if (urlScrollRaw && /^\d+$/.test(urlScrollRaw)) {
      setPendingScrollTop(parseInt(urlScrollRaw, 10));
    }
  }, []);

  const showToast = (message: string, tone: 'success' | 'error' | 'info' = 'info') => {
    if (toastTimerRef.current) {
      clearTimeout(toastTimerRef.current);
    }
    setGlobalToastTone(tone);
    setGlobalToast(message);
    toastTimerRef.current = setTimeout(() => {
      setGlobalToast(null);
      toastTimerRef.current = null;
    }, 1800);
  };

  useEffect(() => {
    // Reset pagination when filters change
    setPage(1);
    setAssets([]);
    setHasMore(true);
    loadAssets(filters, 1, true);
  }, [filters]);

  useEffect(() => {
    applyUrlStateToUi();
    const onPopState = () => {
      applyUrlStateToUi();
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, [applyUrlStateToUi]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setFilters((prev) => {
        if ((prev.search || '') === searchInput) return prev;
        return { ...prev, search: searchInput };
      });
    }, 280);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    if (isApplyingUrlRef.current) return;
    updateUrlState({ q: searchInput });
  }, [searchInput, updateUrlState]);

  useEffect(() => {
    loadBlockedPaths();
    loadStorageStatus();
    loadTagDefinitions();
    loadNasTree();
  }, []);

  useEffect(() => {
    const handleClickOutside = () => setContextMenu((prev) => ({ ...prev, visible: false }));
    window.addEventListener('click', handleClickOutside);
    return () => window.removeEventListener('click', handleClickOutside);
  }, []);

  useEffect(() => {
    return () => {
      if (toastTimerRef.current) {
        clearTimeout(toastTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!isResizingPanel) return;

    const handleMouseMove = (event: MouseEvent) => {
      const delta = event.clientX - panelResizeStartXRef.current;
      const nextWidth = Math.max(240, Math.min(520, panelResizeStartWidthRef.current + delta));
      setPanelWidth(nextWidth);
    };

    const handleMouseUp = () => {
      setIsResizingPanel(false);
    };

    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizingPanel]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const raw = window.localStorage.getItem('linein.material.panelWidth');
    if (!raw) return;
    const parsed = Number(raw);
    if (!Number.isFinite(parsed)) return;
    const safe = Math.max(240, Math.min(520, Math.floor(parsed)));
    setPanelWidth(safe);
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem('linein.material.panelWidth', String(panelWidth));
  }, [panelWidth]);

  // Infinite scroll
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleScroll = () => {
      if (loadingMore || !hasMore) return;

      const { scrollTop, scrollHeight, clientHeight } = container;
      // Trigger when 80% scrolled
      if (scrollTop + clientHeight >= scrollHeight * 0.8) {
        loadMore();
      }
    };

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, [loadingMore, hasMore, page]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    let timer: ReturnType<typeof setTimeout> | null = null;
    const handleUrlScrollSync = () => {
      if (pendingScrollTop !== null) return;
      if (isApplyingUrlRef.current) return;
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        updateUrlState({ scroll: container.scrollTop });
      }, 120);
    };

    container.addEventListener('scroll', handleUrlScrollSync);
    return () => {
      if (timer) clearTimeout(timer);
      container.removeEventListener('scroll', handleUrlScrollSync);
    };
  }, [updateUrlState, pendingScrollTop]);

  const loadStorageStatus = async () => {
    try {
      const status = await apiService.getStorageStatus();
      if (status) {
        setStorageStatus(status);
      }
    } catch (err) {
      console.error('Failed to load storage status:', err);
    }
  };

  const loadBlockedPaths = async () => {
    try {
      const rows = await apiService.getBlockedNasPaths();
      setBlockedNasPaths(rows);
    } catch (err) {
      console.error('Failed to load blocked NAS paths:', err);
    }
  };

  const loadNasTree = async () => {
    try {
      const data = await apiService.getNasTreeFull();
      setNasTreeData(data);
    } catch (err) {
      console.error('Failed to load NAS directory tree:', err);
    }
  };

  const loadTagDefinitions = async () => {
    try {
      const definitions = await apiService.getTagDefinitions();
      setTagDefinitions(definitions);
    } catch (err) {
      console.error('Failed to load tag definitions:', err);
    }
  };

  const normalizeNasPath = (path: string): string => {
    const trimmed = path.trim();
    if (!trimmed) return '';

    if (/^https?:\/\//i.test(trimmed)) return '';

    let normalized = trimmed.replace(/^NAS:\/\//i, '/');
    const gsMatch = normalized.match(/^gs:\/\/[^/]+\/(.*)$/i);
    if (gsMatch?.[1]) {
      normalized = gsMatch[1].startsWith('/') ? gsMatch[1] : `/${gsMatch[1]}`;
    }

    const withSlash = normalized.startsWith('/') ? normalized : `/${normalized}`;
    const collapsed = withSlash.replace(/\/{2,}/g, '/');
    return collapsed.replace(/\/+$/, '') || '/';
  };

  const getAssetPathCandidates = (asset: AssetMetadata, directoriesOnly = false): string[] => {
    const paths: string[] = [];

    const pushPath = (rawPath: string | undefined, isDirectoryPath: boolean) => {
      if (!rawPath) return;
      const normalized = normalizeNasPath(rawPath);
      if (!normalized || normalized === '/') return;

      if (!directoriesOnly) {
        paths.push(normalized);
      }

      if (isDirectoryPath) {
        paths.push(normalized);
        return;
      }

      const lastSlashIndex = normalized.lastIndexOf('/');
      if (lastSlashIndex > 0) {
        const directoryPath = normalized.slice(0, lastSlashIndex);
        if (directoryPath && directoryPath !== '/') {
          paths.push(directoryPath);
        }
      }
    };

    pushPath(asset.nasPath, false);
    pushPath(asset.sourcePath, false);
    pushPath(asset.folderPath, true);

    return Array.from(new Set(paths));
  };

  const isHiddenByPath = (path?: string) => {
    if (!path) return false;
    const normalized = normalizeNasPath(path);
    for (const hiddenPath of hiddenNasPaths) {
      if (normalized === hiddenPath || normalized.startsWith(`${hiddenPath}/`)) {
        return true;
      }
    }
    return false;
  };

  const applyAssetPatch = (assetId: string, patch: Partial<AssetMetadata>) => {
    const applyPatch = (prev: AssetMetadata[]) =>
      prev.map((item) => (item.id === assetId ? { ...item, ...patch } : item));
    setAssets(applyPatch);
    setAllAssets(applyPatch);
    setLightboxAsset((prev) => (prev && prev.id === assetId ? { ...prev, ...patch } : prev));
    setSelectedAsset((prev) => (prev && prev.id === assetId ? { ...prev, ...patch } : prev));
  };

  const copyTextToClipboard = async (text: string): Promise<boolean> => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
        return true;
      }
      const textarea = document.createElement('textarea');
      textarea.value = text;
      document.body.appendChild(textarea);
      textarea.select();
      const copied = document.execCommand('copy');
      document.body.removeChild(textarea);
      return copied;
    } catch {
      return false;
    }
  };

  const getNasFilePathForShare = (asset: AssetMetadata): string => {
    if (asset.nasPath) {
      return normalizeNasPath(asset.nasPath);
    }

    if (!asset.sourcePath) return '';
    const source = asset.sourcePath.trim();
    if (!source) return '';

    if (/^NAS:\/\//i.test(source) || source.startsWith('/linein_share/')) {
      return normalizeNasPath(source);
    }

    return '';
  };

  const handleToggleStar = async (asset: AssetMetadata) => {
    const nextStar = !asset.isStarred;
    applyAssetPatch(asset.id, { isStarred: nextStar });
    try {
      await apiService.updateAsset(asset.id, { isStarred: nextStar });
    } catch (err) {
      applyAssetPatch(asset.id, { isStarred: asset.isStarred });
      showToast('星號更新失敗');
    }
  };

  const handleBatchToggleStar = async (starred: boolean) => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;

    const previousMap = new Map<string, boolean>();
    ids.forEach((id) => {
      const found = allAssets.find((asset) => asset.id === id);
      previousMap.set(id, Boolean(found?.isStarred));
      applyAssetPatch(id, { isStarred: starred });
    });

    try {
      await Promise.all(ids.map((id) => apiService.updateAsset(id, { isStarred: starred })));
      showToast(starred ? '已批次加入星號' : '已批次移除星號');
    } catch (err) {
      ids.forEach((id) => {
        applyAssetPatch(id, { isStarred: previousMap.get(id) || false });
      });
      showToast('批次星號更新失敗');
    }
  };

  const handleToggleNasVisibility = (path: string, visible: boolean) => {
    const normalized = normalizeNasPath(path);
    setHiddenNasPaths((prev) => {
      const next = new Set(prev);
      if (visible) next.delete(normalized);
      else next.add(normalized);
      return next;
    });
  };

  const toggleNasExpand = (path: string) => {
    setExpandedNasPaths((prev) => {
      const next = new Set(prev);
      if (next.has(path)) next.delete(path);
      else next.add(path);
      return next;
    });
  };

  const handleSelectNasPath = (path: string, mode: 'exact' | 'subtree' = 'subtree') => {
    const normalized = normalizeNasPath(path);
    const shouldClear = selectedNasPath === normalized && nasPathMode === mode;
    const nextSelectedPath = shouldClear ? '' : normalized;

    setSelectedNasPath(nextSelectedPath);
    setNasPathMode(mode);
    setPage(1);
    setAssets([]);
    setHasMore(true);
    setFilters((prev) => {
      const next = { ...prev };
      if (nextSelectedPath) {
        next.nasPathPrefix = nextSelectedPath;
        next.nasPathExact = mode === 'exact';
      } else {
        delete next.nasPathPrefix;
        delete next.nasPathExact;
      }
      return next;
    });
  };

  const handleBlockNasPath = async (path: string) => {
    const normalized = normalizeNasPath(path);
    if (!normalized || busyPath) return;
    setBusyPath(normalized);
    try {
      await apiService.blockNasPath(normalized);
      await loadBlockedPaths();
      setPage(1);
      await loadAssets(filters, 1, true, true);
      showToast(`已封鎖路徑：${normalized}`);
    } catch (err) {
      console.error('Block NAS path failed:', err);
      showToast('封鎖路徑失敗');
    } finally {
      setBusyPath(null);
    }
  };

  const handleUnblockNasPath = async (path: string) => {
    const normalized = normalizeNasPath(path);
    if (!normalized || busyPath) return;
    setBusyPath(normalized);
    try {
      await apiService.unblockNasPath(normalized);
      await loadBlockedPaths();
      setPage(1);
      await loadAssets(filters, 1, true, true);
      showToast(`已解除封鎖：${normalized}`);
    } catch (err) {
      console.error('Unblock NAS path failed:', err);
      showToast('解除封鎖失敗');
    } finally {
      setBusyPath(null);
    }
  };

  const loadAssets = async (
    activeFilters?: FilterOptions,
    targetPage: number = 1,
    reset: boolean = false,
    _refreshAll: boolean = false
  ) => {
    const requestId = ++latestAssetsRequestIdRef.current;

    try {
      if (reset) {
        setLoading(true);
      } else {
        setLoadingMore(true);
      }
      setError(null);
      
      const filtersToUse = activeFilters || filters;
      const perPage = filtersToUse.nasPathPrefix ? 400 : 50;
      // Load assets with pagination
      const response = await apiService.getAssets(filtersToUse, targetPage, perPage);
      if (requestId !== latestAssetsRequestIdRef.current) return;
      const rows = response?.data || [];
      
      if (reset) {
        setAssets(rows);
      } else {
        setAssets(prev => [...prev, ...rows]);
      }
      
      // Update hasMore based on response
      setHasMore(rows.length === perPage);
      

    } catch (err: any) {
      if (requestId !== latestAssetsRequestIdRef.current) return;
      console.error('Failed to load assets:', err);
      setError(err.response?.data?.message || '載入素材失敗');
    } finally {
      if (requestId === latestAssetsRequestIdRef.current) {
        setLoading(false);
        setLoadingMore(false);
      }
    }
  };

  const loadMore = () => {
    if (loadingMore || !hasMore) return;
    const nextPage = page + 1;
    setPage(nextPage);
    loadAssets(filters, nextPage, false);
  };

  const handleBatchUpload = async (files: File[], metadata: Partial<AssetMetadata>) => {
    // Initialize upload tasks
    const tasks: UploadTask[] = files.map((file, idx) => ({
      id: `task-${Date.now()}-${idx}`,
      filename: file.name,
      status: 'uploading',
      progress: 0,
      aiProgress: 0,
    }));
    setUploadTasks(tasks);

    // Process files sequentially
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const taskId = tasks[i].id;

      try {
        // Update status to uploading
        setUploadTasks((prev) =>
          prev.map((t) =>
            t.id === taskId ? { ...t, status: 'uploading', progress: 30 } : t
          )
        );

        // Upload file - backend will handle AI analysis and tagging
        const formData = new FormData();
        formData.append('file', file);
        formData.append('uploader', metadata.uploader || '系統管理員');
        if (metadata.activityId) formData.append('activityId', metadata.activityId);

        // Update to analyzing status when upload completes
        setUploadTasks((prev) =>
          prev.map((t) =>
            t.id === taskId ? { ...t, status: 'analyzing', progress: 70 } : t
          )
        );

        const response = await apiService.uploadAsset(formData);

        // Mark as completed with Vision results
        setUploadTasks((prev) =>
          prev.map((t) => 
            t.id === taskId 
              ? { 
                  ...t, 
                  status: 'completed', 
                  progress: 100,
                  visionResult: response.visionAnalysis
                } 
              : t
          )
        );
      } catch (err: any) {
        console.error(`Upload failed for ${file.name}:`, err);
        setUploadTasks((prev) =>
          prev.map((t) =>
            t.id === taskId
              ? { ...t, status: 'error', error: err.message || '上傳失敗' }
              : t
          )
        );
      }
    }

    // Reload assets after all uploads
    setPage(1);
    await loadAssets(filters, 1, true, true);
  };

  const handleUpdate = async (asset: AssetMetadata) => {
    try {
      await apiService.updateAsset(asset.id, asset);
      setPage(1);
      loadAssets(filters, 1, true, true);
    } catch (err) {
      console.error('Update failed:', err);
      alert('更新失敗');
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('確定刪除此素材？')) return;
    try {
      await apiService.deleteAsset(id);
      setPage(1);
      loadAssets(filters, 1, true, true);
      setSelectedAsset(null);
    } catch (err) {
      console.error('Delete failed:', err);
      alert('刪除失敗');
    }
  };

  const handleBatchDelete = async () => {
    if (!confirm(`確定刪除選中的 ${selectedIds.size} 個素材？`)) return;
    try {
      await apiService.deleteAssets(Array.from(selectedIds));
      setPage(1);
      loadAssets(filters, 1, true, true);
      setSelectedIds(new Set());
    } catch (err) {
      console.error('Batch delete failed:', err);
      alert('批次刪除失敗');
    }
  };

  const assetLookup = useMemo(() => {
    const merged = new Map<string, AssetMetadata>();
    allAssets.forEach((asset) => merged.set(asset.id, asset));
    assets.forEach((asset) => merged.set(asset.id, asset));
    return merged;
  }, [allAssets, assets]);

  const selectedAssets = useMemo(() => {
    return Array.from(selectedIds)
      .map((id) => assetLookup.get(id))
      .filter((asset): asset is AssetMetadata => Boolean(asset));
  }, [selectedIds, assetLookup]);

  const openTagModal = (target: TagModalTarget) => {
    if (target.assetIds.length === 0) {
      showToast('目前範圍沒有可操作的素材', 'error');
      return;
    }
    setTagModalTarget(target);
    setTagActionMode('add');
    setSelectedTagKeys(new Set());
    setShowTagModal(true);
  };

  const getAssetIdsByNasFolder = useCallback((folderPath: string): string[] => {
    const ids: string[] = [];
    const normalized = normalizeNasPath(folderPath);

    assetLookup.forEach((asset) => {
      const dirs = getAssetPathCandidates(asset, true);
      const hit = dirs.some((dir) => dir === normalized || dir.startsWith(`${normalized}/`));
      if (hit) ids.push(asset.id);
    });

    return ids;
  }, [assetLookup]);

  const applyTagsToTarget = async (
    mode: 'add' | 'remove',
    targetAssetIds: string[],
    targetTagKeys: string[],
    options: { closeModal?: boolean } = {}
  ) => {
    if (targetAssetIds.length === 0) {
      showToast('目前範圍沒有可操作的素材', 'error');
      return;
    }

    if (targetTagKeys.length === 0) {
      showToast('請先選擇至少一個標籤', 'error');
      return;
    }

    setTagSubmitting(true);
    try {
      await apiService.applyTagsToAssets({
        action: mode,
        assetIds: targetAssetIds,
        tagKeys: targetTagKeys,
      });

      const keyToName = new Map<string, string>();
      tagDefinitions.forEach((tag) => keyToName.set(tag.tagKey, tag.name));

      const applyUpdate = (list: AssetMetadata[]) =>
        list.map((asset) => {
          if (!targetAssetIds.includes(asset.id)) return asset;
          const nextKeys = new Set(getAssetTagKeys(asset));
          if (mode === 'add') {
            targetTagKeys.forEach((key) => nextKeys.add(key));
          } else {
            targetTagKeys.forEach((key) => nextKeys.delete(key));
          }

          const nextTagKeys = Array.from(nextKeys);
          const nextTags = Array.from(
            new Set(nextTagKeys.map((key) => keyToName.get(key) || getTagNameFromKey(key)))
          );

          return {
            ...asset,
            tagKeys: nextTagKeys,
            tags: nextTags,
          } as AssetMetadata;
        });

      setAssets((prev) => applyUpdate(prev));
      setAllAssets((prev) => applyUpdate(prev));
      if (options.closeModal) {
        setShowTagModal(false);
        setSelectedTagKeys(new Set());
      }
      showToast(mode === 'add' ? '標籤已新增' : '標籤已移除', 'success');
    } catch (err) {
      console.error('Apply tags failed:', err);
      showToast('標籤操作失敗', 'error');
    } finally {
      setTagSubmitting(false);
    }
  };

  const handleCreateTagDefinition = async () => {
    const category = newTagCategory.trim();
    const name = newTagName.trim();
    if (!category || !name) {
      showToast('請完整填寫類型 / 名稱', 'error');
      return;
    }

    try {
      const definition = await apiService.createTagDefinition({ category, name });
      setTagDefinitions((prev) => {
        const next = [...prev.filter((tag) => tag.tagKey !== definition.tagKey), definition];
        next.sort((a, b) => getTagLabel(a).localeCompare(getTagLabel(b), 'zh-TW'));
        return next;
      });
      setSelectedTagKeys((prev) => new Set(prev).add(definition.tagKey));
      setNewTagName('');
      showToast('已建立新標籤', 'success');
    } catch (err) {
      console.error('Create tag definition failed:', err);
      showToast('建立標籤失敗', 'error');
    }
  };

  const handleApplyTags = async () => {
    if (!tagModalTarget) return;
    await applyTagsToTarget(tagActionMode, tagModalTarget.assetIds, Array.from(selectedTagKeys), {
      closeModal: true,
    });
  };

  const copyNasDirectoryList = async () => {
    const dirs = new Set<string>();
    selectedAssets.forEach((asset) => {
      const filePath = getNasFilePathForShare(asset);
      if (!filePath) return;
      const idx = filePath.lastIndexOf('/');
      if (idx <= 0) return;
      dirs.add(filePath.slice(0, idx));
    });

    const text = Array.from(dirs).join('\n');
    if (!text) {
      showToast('所選項目沒有可用 NAS 目錄', 'error');
      setShowShareModal(false);
      return;
    }

    const ok = await copyTextToClipboard(text);
    showToast(ok ? `已複製 NAS 目錄清單 (${dirs.size} 筆)` : '複製失敗', ok ? 'success' : 'error');
    setShowShareModal(false);
  };

  const copyNasFileList = async () => {
    const files = new Set<string>();
    selectedAssets.forEach((asset) => {
      const filePath = getNasFilePathForShare(asset);
      if (filePath) files.add(filePath);
    });

    const text = Array.from(files).join('\n');
    if (!text) {
      showToast('所選項目沒有可用 NAS 檔案路徑', 'error');
      setShowShareModal(false);
      return;
    }

    const ok = await copyTextToClipboard(text);
    showToast(ok ? `已複製 NAS 檔案清單 (${files.size} 筆)` : '複製失敗', ok ? 'success' : 'error');
    setShowShareModal(false);
  };

  const copyThumbnailUrls = async () => {
    const urls = new Set<string>();
    selectedAssets.forEach((asset) => {
      if (asset.thumbnailUrl && /^https?:\/\//i.test(asset.thumbnailUrl)) {
        urls.add(asset.thumbnailUrl);
      }
    });

    const text = Array.from(urls).join('\n');
    if (!text) {
      showToast('所選項目沒有可用縮圖網址', 'error');
      setShowShareModal(false);
      return;
    }

    const ok = await copyTextToClipboard(text);
    showToast(ok ? `已複製縮圖網址 (${urls.size} 筆)` : '複製失敗', ok ? 'success' : 'error');
    setShowShareModal(false);
  };

  const copyHdUrls = async () => {
    if (sharingHdLinks) return;
    setSharingHdLinks(true);
    try {
      const hdUrls = new Set<string>();

      for (const asset of selectedAssets) {
        let hdUrl = asset.hdUrl;
        if (!hdUrl || !/^https?:\/\//i.test(hdUrl)) {
          try {
            const promoted = await apiService.promoteAssetToGcpFull(asset.id);
            hdUrl = promoted.hdUrl;
            applyAssetPatch(asset.id, {
              thumbnailUrl: promoted.thumbnailUrl,
              hdUrl: promoted.hdUrl,
              originalUrl: promoted.originalUrl,
            });
          } catch (err) {
            console.error(`Promote HD failed for ${asset.id}:`, err);
          }
        }

        if (hdUrl && /^https?:\/\//i.test(hdUrl)) {
          hdUrls.add(hdUrl);
        }
      }

      const text = Array.from(hdUrls).join('\n');
      if (!text) {
        showToast('所選項目沒有可用高清網址', 'error');
        setShowShareModal(false);
        return;
      }

      const ok = await copyTextToClipboard(text);
      showToast(ok ? `已複製高清網址 (${hdUrls.size} 筆)` : '複製失敗', ok ? 'success' : 'error');
      setShowShareModal(false);
    } finally {
      setSharingHdLinks(false);
    }
  };

  const folderTree = useMemo(() => getFolderTree(assets, extraFolders), [assets, extraFolders]);

  const searchTagTabs = useMemo(() => {
    const categories = Array.from(new Set(tagDefinitions.map((tag) => (tag.category || '').trim()).filter(Boolean)));
    return categories.sort((a, b) => {
      if (a === '產品' && b !== '產品') return -1;
      if (b === '產品' && a !== '產品') return 1;
      if (a === '服務' && b !== '服務') return -1;
      if (b === '服務' && a !== '服務') return 1;
      return a.localeCompare(b, 'zh-TW');
    });
  }, [tagDefinitions]);

  useEffect(() => {
    if (searchTagTabs.length === 0) return;
    if (!searchTagTabs.includes(searchTagTab)) {
      setSearchTagTab(searchTagTabs.includes('產品') ? '產品' : searchTagTabs[0]);
    }
  }, [searchTagTabs, searchTagTab]);

  const searchPanelTags = useMemo(() => {
    if (searchTagTabs.length === 0) return [] as TagDefinition[];
    const activeCategory = searchTagTabs.includes(searchTagTab)
      ? searchTagTab
      : (searchTagTabs.includes('產品') ? '產品' : searchTagTabs[0]);
    return tagDefinitions
      .filter((tag) => tag.category === activeCategory)
      .sort((a, b) => a.name.localeCompare(b.name, 'zh-TW'));
  }, [tagDefinitions, searchTagTab, searchTagTabs]);

  const tagModalScopeAssets = useMemo(() => {
    if (!tagModalTarget) return [] as AssetMetadata[];
    return tagModalTarget.assetIds
      .map((id) => assetLookup.get(id))
      .filter((asset): asset is AssetMetadata => Boolean(asset));
  }, [tagModalTarget, assetLookup]);

  const removableTagKeys = useMemo(() => {
    const keys = new Set<string>();
    tagModalScopeAssets.forEach((asset) => {
      getAssetTagKeys(asset).forEach((key) => keys.add(key));
    });
    return keys;
  }, [tagModalScopeAssets, getAssetTagKeys]);

  const contextMenuAssetIds = useMemo(() => {
    const { type, targetId } = contextMenu;
    if (!targetId) return [] as string[];

    if (type === 'ASSET') {
      return selectedIds.size > 1 && selectedIds.has(targetId)
        ? Array.from(selectedIds)
        : [targetId];
    }

    if (type === 'NAS_FOLDER') {
      return getAssetIdsByNasFolder(targetId);
    }

    return [] as string[];
  }, [contextMenu, selectedIds, getAssetIdsByNasFolder]);

  const contextMenuScopeAssets = useMemo(() => {
    return contextMenuAssetIds
      .map((id) => assetLookup.get(id))
      .filter((asset): asset is AssetMetadata => Boolean(asset));
  }, [contextMenuAssetIds, assetLookup]);

  const contextMenuRemovableTagKeys = useMemo(() => {
    const keys = new Set<string>();
    contextMenuScopeAssets.forEach((asset) => {
      getAssetTagKeys(asset).forEach((key) => keys.add(key));
    });
    return keys;
  }, [contextMenuScopeAssets, getAssetTagKeys]);

  const contextMenuAddTagOptions = useMemo(
    () =>
      tagDefinitions
        .map((tag) => ({ key: tag.tagKey, label: `${tag.category}-${tag.name}` }))
        .sort((a, b) => a.label.localeCompare(b.label, 'zh-TW')),
    [tagDefinitions]
  );

  const contextMenuAddTagTypeOptions = useMemo(() => {
    const grouped = new Map<string, Array<{ key: string; label: string }>>();
    tagDefinitions.forEach((tag) => {
      const typeKey = (tag.category || '').trim() || '未分類';
      const items = grouped.get(typeKey) || [];
      items.push({ key: tag.tagKey, label: tag.name });
      grouped.set(typeKey, items);
    });

    return Array.from(grouped.entries())
      .sort(([a], [b]) => {
        if (a === '產品' && b !== '產品') return -1;
        if (b === '產品' && a !== '產品') return 1;
        if (a === '服務' && b !== '服務') return -1;
        if (b === '服務' && a !== '服務') return 1;
        return a.localeCompare(b, 'zh-TW');
      })
      .map(([typeKey, options]) => ({
        typeKey,
        typeLabel: typeKey,
        options: options.sort((a, b) => a.label.localeCompare(b.label, 'zh-TW')),
      }));
  }, [tagDefinitions]);

  const contextMenuRemoveTagOptions = useMemo(
    () =>
      tagDefinitions
        .filter((tag) => contextMenuRemovableTagKeys.has(tag.tagKey))
        .map((tag) => ({ key: tag.tagKey, label: `${tag.category}-${tag.name}` }))
        .sort((a, b) => a.label.localeCompare(b.label, 'zh-TW')),
    [tagDefinitions, contextMenuRemovableTagKeys]
  );

  const nasPathTree = useMemo((): NasTreeNode[] => {
    if (activePanel !== 'nas' || nasTreeData.length === 0) return [];

    type MutableNasNode = NasTreeNode & { _childrenMap: Map<string, MutableNasNode> };
    const nodeMap = new Map<string, MutableNasNode>();

    // Build node map from DB rows
    for (const item of nasTreeData) {
      nodeMap.set(item.path, {
        name: item.name,
        path: item.path,
        directCount: item.directAssetCount,
        totalCount: item.totalAssetCount,
        children: [],
        _childrenMap: new Map(),
      });
    }

    // Wire children to parents
    const roots: MutableNasNode[] = [];
    for (const item of nasTreeData) {
      const node = nodeMap.get(item.path)!;
      const parentPath =
        item.parentPath !== undefined
          ? item.parentPath
          : item.path.lastIndexOf('/') > 0
          ? item.path.substring(0, item.path.lastIndexOf('/'))
          : null;
      if (parentPath && nodeMap.has(parentPath)) {
        nodeMap.get(parentPath)!._childrenMap.set(item.path, node);
      } else {
        roots.push(node);
      }
    }

    // Sort and finalize (strip internal _childrenMap, sort by name)
    const finalize = (nodes: MutableNasNode[]): NasTreeNode[] =>
      nodes
        .sort((a, b) => a.name.localeCompare(b.name, 'zh-TW'))
        .map((n) => ({
          name: n.name,
          path: n.path,
          directCount: n.directCount,
          totalCount: n.totalCount,
          children: finalize(Array.from(n._childrenMap.values())),
        }));

    return finalize(roots);
  }, [nasTreeData, activePanel]);

  useEffect(() => {
    if (nasPathTree.length === 0) return;
    setExpandedNasPaths((prev) => {
      if (prev.size > 0) return prev;
      return new Set(nasPathTree.map((node) => node.path));
    });
  }, [nasPathTree]);

  useEffect(() => {
    if (pendingScrollTop === null) return;
    const container = containerRef.current;
    if (!container) return;

    const target = Math.max(0, pendingScrollTop);
    const maxScrollable = Math.max(0, container.scrollHeight - container.clientHeight);

    if (maxScrollable >= target || !hasMore) {
      container.scrollTop = Math.min(target, maxScrollable);
      setPendingScrollTop(null);
      return;
    }

    if (!loading && !loadingMore && hasMore) {
      loadMore();
    }
  }, [pendingScrollTop, assets.length, loading, loadingMore, hasMore]);

  const filteredAssets = useMemo(() => {
    return assets.filter((asset) => {
      const directoryPaths = getAssetPathCandidates(asset, true);

      // Only filter by hidden paths - don't re-filter by selectedNasPath 
      // since the backend already handles nasPathPrefix filtering
      if (directoryPaths.some((path) => isHiddenByPath(path))) return false;
      
      return true;
    });
  }, [assets, hiddenNasPaths]);

  const openLightbox = useCallback(
    (asset: AssetMetadata, mode: 'replace' | 'push' = 'replace') => {
      setLightboxAsset(asset);
      updateUrlState({ photo: asset.id }, mode);
    },
    [updateUrlState]
  );

  const closeLightbox = useCallback(() => {
    setLightboxAsset(null);
    updateUrlState({ photo: null });
  }, [updateUrlState]);

  const openEditModalWithLatest = useCallback(
    async (asset: AssetMetadata) => {
      closeLightbox();
      try {
        const latest = await apiService.getAssetById(asset.id);
        setAllAssets((prev) => prev.map((item) => (item.id === latest.id ? latest : item)));
        setAssets((prev) => prev.map((item) => (item.id === latest.id ? latest : item)));
        setSelectedAsset(latest);
      } catch (err) {
        console.error('Failed to refresh asset before edit:', err);
        setSelectedAsset(asset);
        showToast('取得最新素材狀態失敗，已使用目前資料開啟', 'error');
      }
    },
    [closeLightbox]
  );

  useEffect(() => {
    if (!pendingDeepLinkPhotoId) return;

    const existing = allAssets.find((item) => item.id === pendingDeepLinkPhotoId)
      || assets.find((item) => item.id === pendingDeepLinkPhotoId);
    if (existing) {
      openLightbox(existing);
      setPendingDeepLinkPhotoId(null);
      return;
    }

    let cancelled = false;
    apiService
      .getAssetById(pendingDeepLinkPhotoId)
      .then((asset) => {
        if (cancelled) return;
        setAllAssets((prev) => (prev.some((a) => a.id === asset.id) ? prev : [asset, ...prev]));
        setAssets((prev) => (prev.some((a) => a.id === asset.id) ? prev : [asset, ...prev]));
        openLightbox(asset);
      })
      .catch((err) => {
        if (!cancelled) {
          console.error('Failed to open deep-linked photo:', err);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setPendingDeepLinkPhotoId(null);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [pendingDeepLinkPhotoId, allAssets, assets, openLightbox]);

  const lightboxAssets = useMemo(() => {
    if (!lightboxAsset) return filteredAssets;
    if (filteredAssets.some((item) => item.id === lightboxAsset.id)) return filteredAssets;
    return [lightboxAsset, ...filteredAssets];
  }, [lightboxAsset, filteredAssets]);

  // Context Menu Handlers
  const handleAssetContextMenu = (e: React.MouseEvent, id: string) => {
    e.preventDefault();
    e.stopPropagation();
    setSelectedIds((prev) => {
      if (prev.size > 1 && prev.has(id)) return prev;
      return new Set([id]);
    });
    setContextMenu({
      x: e.clientX,
      y: e.clientY,
      visible: true,
      type: 'ASSET',
      targetId: id,
    });
  };

  const handleNasFolderContextMenu = (e: React.MouseEvent, folderPath: string) => {
    e.preventDefault();
    e.stopPropagation();
    setContextMenu({
      x: e.clientX,
      y: e.clientY,
      visible: true,
      type: 'NAS_FOLDER',
      targetId: folderPath,
    });
  };

  const handleFolderContextMenu = (e: React.MouseEvent, folderPath: string) => {
    e.preventDefault();
    e.stopPropagation();
    setContextMenu({
      x: e.clientX,
      y: e.clientY,
      visible: true,
      type: 'FOLDER',
      targetId: folderPath,
    });
  };

  const handleBackgroundContextMenu = (e: React.MouseEvent) => {
    e.preventDefault();
    setContextMenu({ x: e.clientX, y: e.clientY, visible: true, type: 'BACKGROUND' });
  };

  const handleAssetDrop = (assetId: string, folderPath: string) => {
    const updateFn = (prev: AssetMetadata[]) =>
      prev.map((asset) => {
        if (asset.id === assetId) {
          // Update folderPath directly
          return {
            ...asset,
            folderPath: folderPath,
            modifiedDate: new Date().toISOString(),
          };
        }
        return asset;
      });
    
    setAssets(updateFn);
    setAllAssets(updateFn);
  };

  const executeContextAction = (action: string) => {
    const { targetId, type } = contextMenu;

    if ((type === 'ASSET' || type === 'NAS_FOLDER') && action.startsWith('tag:')) {
      const contextIds = contextMenuAssetIds;
      const prefix = action.startsWith('tag:add:') ? 'tag:add:' : action.startsWith('tag:remove:') ? 'tag:remove:' : null;
      if (prefix) {
        const encodedKey = action.slice(prefix.length);
        const decodedKey = decodeURIComponent(encodedKey);
        void applyTagsToTarget(prefix === 'tag:add:' ? 'add' : 'remove', contextIds, [decodedKey]);
      }
    }

    if (type === 'ASSET' && targetId) {
      const targetAsset = assets.find((a) => a.id === targetId);
      const contextAssetIds = selectedIds.size > 1 && selectedIds.has(targetId)
        ? Array.from(selectedIds)
        : [targetId];
      if (action === 'manageTags') {
        openTagModal({
          type: 'assets',
          title: contextAssetIds.length > 1 ? `已選 ${contextAssetIds.length} 個素材` : '素材標籤管理',
          assetIds: contextAssetIds,
        });
      }
      if (action === 'view' && targetAsset) {
        setLightboxAsset(targetAsset);
      }
      if (action === 'edit' && targetAsset && contextAssetIds.length === 1) {
        void openEditModalWithLatest(targetAsset);
      }
      if (action === 'blockFolder' && targetAsset) {
        const filePath = getNasFilePathForShare(targetAsset);
        if (!filePath) {
          showToast('此素材沒有可封鎖的 NAS 路徑', 'error');
        } else {
          const idx = filePath.lastIndexOf('/');
          const folderPath = idx > 0 ? filePath.slice(0, idx) : '';
          if (!folderPath) {
            showToast('無法判斷目錄路徑', 'error');
          } else {
            void handleBlockNasPath(folderPath);
          }
        }
      }
      if (action === 'blockFile' && targetAsset) {
        const filePath = getNasFilePathForShare(targetAsset);
        if (!filePath) {
          showToast('此素材沒有可封鎖的 NAS 路徑', 'error');
        } else {
          void handleBlockNasPath(filePath);
        }
      }
      if (action === 'delete') {
        handleDelete(targetId);
      }
    } else if (type === 'FOLDER' && targetId) {
      if (action === 'addFolder') {
        const name = prompt('輸入新資料夾名稱');
        if (name) setExtraFolders([...extraFolders, `${targetId}/${name}`]);
      } else if (action === 'rename') {
        const oldPath = targetId;
        const currentName = oldPath.split('/').pop() || '';
        const newName = prompt('輸入新資料夾名稱', currentName);

        if (newName && newName !== currentName) {
          const pathParts = oldPath.split('/');
          pathParts.pop();
          const parent = pathParts.join('/');
          const newPath = parent ? `${parent}/${newName}` : newName;

          setAssets((prev) =>
            prev.map((a) => {
              if (!a.sourcePath) return a;
              const bucketMatch = a.sourcePath.match(/^gs:\/\/[^\/]+\//);
              if (!bucketMatch) return a;
              const prefix = bucketMatch[0];
              const relPath = a.sourcePath.substring(prefix.length);

              if (relPath === oldPath || relPath.startsWith(oldPath + '/')) {
                const updatedRelPath = newPath + relPath.substring(oldPath.length);
                return { ...a, sourcePath: prefix + updatedRelPath };
              }
              return a;
            })
          );

          setExtraFolders((prev) =>
            prev.map((f) => {
              if (f === oldPath || f.startsWith(oldPath + '/')) {
                return newPath + f.substring(oldPath.length);
              }
              return f;
            })
          );

          if (
            filters.folder &&
            (filters.folder === oldPath || filters.folder.startsWith(oldPath + '/'))
          ) {
            setFilters((prev) => ({
              ...prev,
              folder: newPath + prev.folder!.substring(oldPath.length),
            }));
          }
        }
      } else if (action === 'cut') {
        setClipboardFolder({ path: targetId, action: 'CUT' });
      } else if (action === 'paste' && clipboardFolder) {
        const sourcePath = clipboardFolder.path;
        const targetParentPath = targetId;
        const folderName = sourcePath.split('/').pop()!;
        const finalDestPath = `${targetParentPath}/${folderName}`;

        if (finalDestPath.startsWith(sourcePath)) {
          alert('無法將資料夾貼上至其子目錄中');
          return;
        }

        setAssets((prev) =>
          prev.map((a) => {
            if (!a.sourcePath) return a;
            const bucketMatch = a.sourcePath.match(/^gs:\/\/[^\/]+\//);
            if (!bucketMatch) return a;
            const prefix = bucketMatch[0];
            const relPath = a.sourcePath.substring(prefix.length);

            if (relPath === sourcePath || relPath.startsWith(sourcePath + '/')) {
              const updatedRelPath = finalDestPath + relPath.substring(sourcePath.length);
              return { ...a, sourcePath: prefix + updatedRelPath };
            }
            return a;
          })
        );

        setExtraFolders((prev) =>
          prev.map((f) => {
            if (f === sourcePath || f.startsWith(sourcePath + '/')) {
              return finalDestPath + f.substring(sourcePath.length);
            }
            return f;
          })
        );

        setClipboardFolder(null);
      } else if (action === 'deleteFolder') {
        if (confirm(`確定刪除資料夾「${targetId}」及其內所有素材？`)) {
          setAssets((prev) =>
            prev.filter((a) => {
              if (!a.sourcePath) return true;
              const bucketMatch = a.sourcePath.match(/^gs:\/\/[^\/]+\//);
              const relPath = bucketMatch ? a.sourcePath.substring(bucketMatch[0].length) : '';
              return !(relPath === targetId || relPath.startsWith(targetId + '/'));
            })
          );
          setExtraFolders((prev) =>
            prev.filter((f) => !(f === targetId || f.startsWith(targetId + '/')))
          );
          if (
            filters.folder &&
            (filters.folder === targetId || filters.folder.startsWith(targetId + '/'))
          ) {
            setFilters((prev) => ({ ...prev, folder: '' }));
          }
        }
      }
    } else if (type === 'NAS_FOLDER' && targetId) {
      if (action === 'manageNasTags') {
        const ids = getAssetIdsByNasFolder(targetId);
        openTagModal({
          type: 'nas-folder',
          title: `NAS 目錄：${targetId}`,
          assetIds: ids,
        });
      } else if (action === 'clearNasFilter') {
        if (selectedNasPath) {
          handleSelectNasPath(selectedNasPath, nasPathMode);
        }
      }
    } else if (type === 'BACKGROUND') {
      if (action === 'selectAll') setSelectedIds(new Set(assets.map((a) => a.id)));
      if (action === 'upload') setShowUploadModal(true);
    }

    setContextMenu((prev) => ({ ...prev, visible: false }));
  };

  // Marquee Selection Handlers
  const handleMouseDown = (e: React.MouseEvent) => {
    if (e.button !== 0 || (e.target as HTMLElement).closest('.asset-card')) return;
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;

    const scrollLeft = containerRef.current?.scrollLeft || 0;
    const scrollTop = containerRef.current?.scrollTop || 0;

    setMarquee({
      x1: e.clientX - rect.left + scrollLeft,
      y1: e.clientY - rect.top + scrollTop,
      x2: e.clientX - rect.left + scrollLeft,
      y2: e.clientY - rect.top + scrollTop,
    });

    if (!e.ctrlKey && !e.shiftKey) setSelectedIds(new Set());
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!marquee || !containerRef.current) return;

    const rect = containerRef.current.getBoundingClientRect();
    const scrollLeft = containerRef.current.scrollLeft;
    const scrollTop = containerRef.current.scrollTop;

    const curX = e.clientX - rect.left + scrollLeft;
    const curY = e.clientY - rect.top + scrollTop;

    setMarquee((prev) => (prev ? { ...prev, x2: curX, y2: curY } : null));

    const xMin = Math.min(marquee.x1, curX);
    const xMax = Math.max(marquee.x1, curX);
    const yMin = Math.min(marquee.y1, curY);
    const yMax = Math.max(marquee.y1, curY);

    const newSelected = new Set(selectedIds);
    const cards = containerRef.current.querySelectorAll('.asset-card[data-id]');

    cards.forEach((card: any) => {
      const cardId = card.getAttribute('data-id');
      const cardRect = {
        left: card.offsetLeft,
        top: card.offsetTop,
        right: card.offsetLeft + card.offsetWidth,
        bottom: card.offsetTop + card.offsetHeight,
      };

      const isIntersect = !(
        cardRect.left > xMax ||
        cardRect.right < xMin ||
        cardRect.top > yMax ||
        cardRect.bottom < yMin
      );

      if (isIntersect) newSelected.add(cardId);
      else if (!e.ctrlKey) newSelected.delete(cardId);
    });

    setSelectedIds(newSelected);
  };

  const handleMouseUp = () => setMarquee(null);

  const renderNasTree = (nodes: NasTreeNode[], depth = 0): React.ReactNode => {
    const hasKeyword = nasTreeKeyword.trim().length > 0;
    return nodes.map((node) => {
      const hidden = isHiddenByPath(node.path);
      const hasChildren = node.children.length > 0;
      const expanded = hasKeyword || expandedNasPaths.has(node.path);
      const selectedExact = selectedNasPath === node.path && nasPathMode === 'exact';
      const selectedSubtree = selectedNasPath === node.path && nasPathMode === 'subtree';
      const selected = selectedExact || selectedSubtree;

      return (
        <div key={node.path}>
          <div
            className={`group flex items-center gap-1 py-1 rounded-md cursor-pointer transition-colors ${
              selectedSubtree
                ? 'bg-indigo-100'
                : selectedExact
                  ? 'bg-amber-50'
                  : 'hover:bg-slate-100'
            } ${hidden ? 'opacity-40' : ''}`}
            style={{ paddingLeft: `${depth * 14 + 4}px` }}
            onContextMenu={(e) => handleNasFolderContextMenu(e, node.path)}
          >
            {/* Expand/collapse toggle */}
            <button
              className="w-4 h-4 shrink-0 flex items-center justify-center text-slate-400 hover:text-slate-600"
              onClick={(e) => {
                e.stopPropagation();
                if (hasChildren) toggleNasExpand(node.path);
              }}
              disabled={!hasChildren}
            >
              {hasChildren
                ? expanded
                  ? <ChevronDown size={11} />
                  : <ChevronRight size={11} />
                : <span className="w-2" />}
            </button>

            {/* Eye visibility toggle */}
            <button
              className="w-4 h-4 shrink-0 flex items-center justify-center"
              onClick={(e) => {
                e.stopPropagation();
                handleToggleNasVisibility(node.path, hidden);
              }}
              title={hidden ? '恢復顯示此目錄' : '隱藏此目錄'}
            >
              {hidden
                ? <EyeOff size={12} className="text-slate-300" />
                : <Eye size={12} className="text-slate-400 opacity-0 group-hover:opacity-100 transition-opacity" />}
            </button>

            {/* Directory name — click = exact (current folder only) */}
            <span
              className={`flex-1 min-w-0 text-[11px] font-medium truncate select-none ${
                selected ? 'font-bold' : ''
              } ${
                selectedSubtree
                  ? 'text-indigo-700'
                  : selectedExact
                    ? 'text-amber-700'
                    : 'text-slate-700'
              }`}
              title={`${node.path}\n點擊：顯示此目錄及所有子目錄的素材`}
              onClick={() => handleSelectNasPath(node.path, 'subtree')}
            >
              {node.name}
            </span>

            {/* Direct count badge — click = exact mode */}
            {node.directCount > 0 && (
              <button
                onClick={(e) => { e.stopPropagation(); handleSelectNasPath(node.path, 'exact'); }}
                title="點擊：僅顯示此目錄的素材"
                className={`shrink-0 min-w-[18px] px-1 h-4 rounded text-[9px] font-bold leading-4 transition-colors ${
                  selectedExact
                    ? 'bg-amber-500 text-white'
                    : 'bg-slate-200 text-slate-600 hover:bg-amber-400 hover:text-white'
                }`}
              >
                {node.directCount}
              </button>
            )}

            {/* Total count badge — click = subtree mode (only when > directCount) */}
            {node.totalCount > node.directCount && (
              <button
                onClick={(e) => { e.stopPropagation(); handleSelectNasPath(node.path, 'subtree'); }}
                title="點擊：顯示此目錄及所有子目錄的素材"
                className={`shrink-0 min-w-[18px] px-1 h-4 rounded text-[9px] font-bold leading-4 transition-colors ${
                  selectedSubtree
                    ? 'bg-indigo-500 text-white'
                    : 'bg-indigo-100 text-indigo-600 hover:bg-indigo-500 hover:text-white'
                }`}
              >
                {node.totalCount}
              </button>
            )}

            {/* Block button */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleBlockNasPath(node.path);
              }}
              disabled={busyPath === node.path}
              className="shrink-0 w-5 h-5 flex items-center justify-center rounded text-slate-300 hover:text-red-500 hover:bg-red-50 opacity-0 group-hover:opacity-100 transition-all disabled:opacity-30"
              title="永久封鎖此目錄（從搜尋結果永久移除）"
            >
              <Ban size={11} />
            </button>
          </div>
          {hasChildren && expanded && renderNasTree(node.children, depth + 1)}
        </div>
      );
    });
  };

  const filteredNasPathTree = useMemo(() => {
    const keyword = nasTreeKeyword.trim().toLowerCase();
    if (!keyword) return nasPathTree;

    const filterNodes = (nodes: NasTreeNode[]): NasTreeNode[] =>
      nodes.flatMap((node) => {
        const children = filterNodes(node.children);
        const matched = node.path.toLowerCase().includes(keyword);
        if (!matched && children.length === 0) {
          return [];
        }
        return [{ ...node, children }];
      });

    return filterNodes(nasPathTree);
  }, [nasPathTree, nasTreeKeyword]);

  const countNasFolders = (nodes: NasTreeNode[]): number =>
    nodes.reduce((sum, node) => sum + 1 + countNasFolders(node.children), 0);

  const togglePanel = (panel: 'search' | 'nas' | 'settings') => {
    setActivePanel((prev) => (prev === panel ? null : panel));
  };

  const startResizePanel = (event: React.MouseEvent<HTMLDivElement>) => {
    panelResizeStartXRef.current = event.clientX;
    panelResizeStartWidthRef.current = panelWidth;
    setIsResizingPanel(true);
  };

  return (
    <div className="flex h-screen bg-slate-50 overflow-hidden font-sans select-none text-slate-900">
      {/* Activity Bar - Column 1 */}
      <div className="w-12 bg-[#1e1e1e] flex flex-col items-center py-3 shrink-0 z-30">
        {/* App Logo */}
        <div className="w-9 h-9 bg-indigo-600 rounded-xl flex items-center justify-center text-white mb-6 shrink-0">
          <LayoutDashboard size={18} />
        </div>

        {/* Activity Icons */}
        <nav className="flex-1 flex flex-col items-center gap-1">
          <button
            title="搜尋"
            onClick={() => togglePanel('search')}
            className={`relative w-10 h-10 flex items-center justify-center rounded-lg transition-all ${
              activePanel === 'search'
                ? 'text-white'
                : 'text-slate-400 hover:text-white hover:bg-white/10'
            }`}
          >
            {activePanel === 'search' && (
              <span className="absolute left-0 top-1 bottom-1 w-0.5 bg-white rounded-r-full" />
            )}
            <Search size={22} />
          </button>
          <button
            title="NAS 目錄"
            onClick={() => togglePanel('nas')}
            className={`relative w-10 h-10 flex items-center justify-center rounded-lg transition-all ${
              activePanel === 'nas'
                ? 'text-white'
                : 'text-slate-400 hover:text-white hover:bg-white/10'
            }`}
          >
            {activePanel === 'nas' && (
              <span className="absolute left-0 top-1 bottom-1 w-0.5 bg-white rounded-r-full" />
            )}
            <HardDrive size={22} />
          </button>
          <button
            title="系統設定"
            onClick={() => togglePanel('settings')}
            className={`relative w-10 h-10 flex items-center justify-center rounded-lg transition-all ${
              activePanel === 'settings'
                ? 'text-white'
                : 'text-slate-400 hover:text-white hover:bg-white/10'
            }`}
          >
            {activePanel === 'settings' && (
              <span className="absolute left-0 top-1 bottom-1 w-0.5 bg-white rounded-r-full" />
            )}
            <Settings size={22} />
          </button>
        </nav>
      </div>

      {/* Side Panel - Column 2 */}
      {activePanel && (
        <aside
          className="bg-white border-r border-slate-200 flex flex-col z-20 shrink-0 overflow-hidden relative"
          style={{ width: `${panelWidth}px` }}
        >

          {/* ── Search Panel ── */}
          {activePanel === 'search' && (
            <div className="flex flex-col h-full overflow-hidden">
              <div className="px-4 pt-4 pb-3 border-b border-slate-200 shrink-0">
                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-3">搜尋</p>
                <div className="relative mb-2">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={14} />
                  <input
                    type="text"
                    placeholder="搜尋素材、路徑或標籤..."
                    className="w-full pl-8 pr-3 py-2 bg-slate-50 border border-slate-100 rounded-lg focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 outline-none font-bold text-xs text-slate-800"
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                  />
                </div>
                <button
                  onClick={() => setFilters((prev) => ({ ...prev, starredOnly: !prev.starredOnly }))}
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border text-xs font-bold w-full ${
                    filters.starredOnly
                      ? 'border-amber-300 bg-amber-50 text-amber-700'
                      : 'border-slate-200 bg-white hover:bg-slate-50 text-slate-600'
                  }`}
                >
                  <Star size={12} className={filters.starredOnly ? 'fill-amber-400 text-amber-500' : ''} />
                  僅顯示星號
                </button>
              </div>

              <div className="flex-1 overflow-y-auto px-4 py-3">
                {/* Folder Tree */}
                <section className="mb-5">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">目錄結構</p>
                    <button
                      onClick={() => {
                        const name = prompt('新增根目錄名稱');
                        if (name) setExtraFolders([...extraFolders, name]);
                      }}
                      className="p-1 hover:bg-slate-100 rounded text-indigo-600"
                    >
                      <FolderPlus size={12} />
                    </button>
                  </div>
                  <div className="space-y-0.5">
                    {Object.keys(folderTree).map((key) => (
                      <FolderTreeItem
                        key={key}
                        name={key}
                        path=""
                        children={folderTree[key].children}
                        onSelect={(p) => setFilters({ ...filters, folder: p })}
                        onDrop={handleAssetDrop}
                        onContextMenu={handleFolderContextMenu}
                        currentPath={filters.folder || ''}
                      />
                    ))}
                    {Object.keys(folderTree).length === 0 && (
                      <p className="text-[10px] italic text-slate-400">尚無目錄資訊</p>
                    )}
                  </div>
                </section>

                {/* Tags */}
                <section className="mb-5">
                  <div className="mb-2">
                    <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">標籤系統</p>
                  </div>

                  {searchTagTabs.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mb-2">
                      {searchTagTabs.map((tab) => (
                        <button
                          key={`search-tag-tab-${tab}`}
                          onClick={() => setSearchTagTab(tab)}
                          className={`px-2.5 py-1 rounded-md text-[10px] font-bold border transition-colors ${
                            searchTagTab === tab
                              ? 'bg-indigo-600 text-white border-indigo-600'
                              : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
                          }`}
                        >
                          {tab}
                        </button>
                      ))}
                    </div>
                  )}

                  <div className="flex flex-wrap gap-1.5">
                    {searchPanelTags.map((tag) => {
                      const active = filters.tag === tag.tagKey;
                      return (
                      <button
                        key={tag.tagKey}
                        onClick={() => setFilters({ ...filters, tag: filters.tag === tag.tagKey ? '' : tag.tagKey })}
                        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full font-bold text-[11px] border transition-colors ${
                          active
                            ? 'bg-indigo-50 text-indigo-700 border-indigo-200'
                            : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
                        }`}
                        title={`${tag.category}-${tag.name}`}
                      >
                        <span>{tag.name}</span>
                        <span className={`inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] ${
                          active
                            ? 'bg-indigo-600 text-white'
                            : 'bg-slate-100 text-slate-500'
                        }`}>
                          {tag.usageCount || 0}
                        </span>
                      </button>
                    );
                    })}
                    {searchTagTabs.length === 0 && (
                      <p className="text-[10px] italic text-slate-400">尚無標籤定義</p>
                    )}
                  </div>
                </section>
              </div>
            </div>
          )}

          {/* ── NAS Panel ── */}
          {activePanel === 'nas' && (
            <div className="flex flex-col h-full overflow-hidden">
              {/* Panel header */}
              <div className="px-4 pt-4 pb-3 border-b border-slate-200 shrink-0">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">NAS 目錄</p>
                  <div className="flex items-center gap-2">
                    {selectedNasPath && (
                      <button
                        onClick={() => handleSelectNasPath(selectedNasPath, nasPathMode)}
                        className="text-[10px] font-bold text-amber-600 hover:text-amber-700 flex items-center gap-1"
                      >
                        <X size={10} /> 清除篩選
                      </button>
                    )}
                    <button
                      onClick={() => setHiddenNasPaths(new Set())}
                      className="text-[10px] font-bold text-slate-400 hover:text-slate-600 flex items-center gap-1"
                    >
                      <Eye size={10} /> 全部顯示
                    </button>
                  </div>
                </div>
                <input
                  type="text"
                  value={nasTreeKeyword}
                  onChange={(e) => setNasTreeKeyword(e.target.value)}
                  placeholder="搜尋目錄..."
                  className="w-full px-3 py-1.5 text-xs bg-slate-50 border border-slate-200 rounded-lg outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500"
                />
              </div>

              {/* Tree */}
              <div className="flex-1 overflow-y-auto px-2 py-2">
                {filteredNasPathTree.length > 0 ? (
                  <div className="space-y-0">{renderNasTree(filteredNasPathTree)}</div>
                ) : (
                  <p className="px-3 py-6 text-xs text-slate-400 text-center">
                    {nasTreeKeyword.trim()
                      ? '沒有符合關鍵字的目錄'
                      : activePanel === 'nas'
                        ? '載入中或尚無素材目錄資料'
                        : ''}
                  </p>
                )}
              </div>
            </div>
          )}

          {/* ── Settings Panel ── */}
          {activePanel === 'settings' && (
            <div className="flex flex-col h-full overflow-hidden">
              <div className="px-4 pt-4 pb-3 border-b border-slate-200 shrink-0">
                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">系統設定</p>
              </div>
              <div className="flex-1 overflow-y-auto px-4 py-3">
                {/* Storage */}
                <div className="p-3 bg-gradient-to-br from-indigo-50 to-blue-50 rounded-xl border border-indigo-100 mb-4">
                  <div className="flex items-center gap-2 mb-1.5">
                    <Cloud size={14} className="text-indigo-600" />
                    <h3 className="font-black text-xs text-indigo-900">儲存空間</h3>
                  </div>
                  <div className="h-1.5 bg-white/50 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-indigo-600 rounded-full transition-all duration-500"
                      style={{ width: `${storageStatus.usagePercentage}%` }}
                    />
                  </div>
                  <p className="text-[10px] text-indigo-600 font-bold mt-1.5">
                    {storageStatus.usedGB.toFixed(1)}GB / {storageStatus.totalGB}GB
                  </p>
                </div>

                {/* NAS Sync */}
                <div className="mb-4">
                  <NasSyncPanel
                    onSyncComplete={() => {
                      setPage(1);
                      loadAssets(filters, 1, true, true);
                      loadNasTree();
                    }}
                  />
                </div>

                <h3 className="font-black text-sm text-slate-800 mb-3">已封鎖 NAS 路徑</h3>
                <div className="space-y-2">
                  {blockedNasPaths.length === 0 && (
                    <p className="text-xs text-slate-400">目前沒有封鎖路徑</p>
                  )}
                  {blockedNasPaths.map((item) => (
                    <div key={item.path} className="border border-slate-200 rounded-lg p-3">
                      <p className="text-xs font-bold text-slate-700 break-all select-text cursor-text">{item.path}</p>
                      <div className="mt-2 flex items-center justify-between">
                        <span className="text-[10px] text-slate-400">
                          {new Date(item.createdAt).toLocaleString('zh-TW')}
                        </span>
                        <button
                          onClick={() => handleUnblockNasPath(item.path)}
                          disabled={busyPath === item.path}
                          className="text-[11px] font-bold text-red-500 hover:text-red-600 disabled:opacity-40"
                        >
                          解除封鎖
                        </button>
                      </div>
    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          <div
            className="absolute top-0 right-0 h-full w-1.5 cursor-col-resize hover:bg-indigo-200/70 active:bg-indigo-300/80"
            onMouseDown={startResizePanel}
            title="拖曳調整工具列寬度"
          />
        </aside>
      )}

      {/* Main Content - Column 3 */}
      <main className="flex-1 flex flex-col overflow-hidden relative">
        {/* Header */}
        <header className="h-13 bg-white border-b border-slate-200 px-6 flex items-center justify-between shrink-0 z-20" style={{height: '52px'}}>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setFilters({ ...filters, sourceType: 'ALL', tag: '', folder: '', starredOnly: false })}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
                filters.sourceType === 'ALL' && !filters.tag && !filters.folder && !filters.starredOnly
                  ? 'bg-slate-900 text-white'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              <LayoutDashboard size={13} /> 全部素材
            </button>
            <div className="flex items-center gap-2 bg-slate-50 px-3 py-1.5 rounded-lg border border-slate-100">
              <span className="text-xs font-bold text-slate-400">大小</span>
              <input
                type="range"
                min="150"
                max="400"
                step="10"
                value={cardScale}
                onChange={(e) => setCardScale(parseInt(e.target.value))}
                className="w-28 accent-indigo-600"
              />
            </div>
          </div>
          <button
            onClick={() => setShowUploadModal(true)}
            className="bg-indigo-600 text-white px-5 py-2 rounded-xl font-black shadow-lg shadow-indigo-100 hover:bg-indigo-700 transition-all flex items-center gap-2 text-sm"
          >
            <Plus size={16} /> 上傳
          </button>
        </header>

        {/* Content Area */}
        <div
          ref={containerRef}
          className="flex-1 overflow-y-auto p-8 relative bg-slate-50/50"
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onContextMenu={handleBackgroundContextMenu}
        >
          <MarqueeSelection marquee={marquee} />

          {loading && (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <div className="w-16 h-16 border-4 border-indigo-500/20 border-t-indigo-500 rounded-full animate-spin mx-auto mb-4"></div>
                <p className="text-slate-500 font-bold">載入中...</p>
              </div>
            </div>
          )}

          {error && (
            <div className="flex items-center justify-center h-full">
              <div className="bg-red-50 border-2 border-red-200 rounded-2xl p-8 max-w-md">
                <AlertCircle size={48} className="text-red-500 mx-auto mb-4" />
                <h3 className="font-black text-red-900 text-center mb-2">載入失敗</h3>
                <p className="text-red-700 text-sm text-center mb-4">{error}</p>
                <button
                  onClick={() => loadAssets(filters, 1, true)}
                  className="w-full bg-red-500 text-white py-2 rounded-xl font-bold hover:bg-red-600 transition-all"
                >
                  重試
                </button>
              </div>
            </div>
          )}

          {!loading && !error && filteredAssets.length === 0 && (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <div className="w-24 h-24 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Cloud size={48} className="text-slate-400" />
                </div>
                <h3 className="font-black text-slate-700 text-xl mb-2">尚無素材</h3>
                <p className="text-slate-500 mb-6">開始上傳您的第一個素材</p>
                <button
                  onClick={() => setShowUploadModal(true)}
                  className="bg-indigo-600 text-white px-8 py-3 rounded-2xl font-black shadow-lg hover:bg-indigo-700 transition-all flex items-center gap-2 mx-auto"
                >
                  <Plus size={20} /> 上傳素材
                </button>
              </div>
            </div>
          )}

          {!loading && !error && filteredAssets.length > 0 && (
            <>
              <div
                className="grid transition-all duration-300"
                style={{
                  gridTemplateColumns: `repeat(auto-fill, minmax(${cardScale}px, 1fr))`,
                  gap: `${Math.max(8, (cardScale - 100) / 15)}px`,
                }}
              >
                {filteredAssets.map((asset) => (
                  <div
                    key={asset.id}
                    className="asset-card"
                    data-id={asset.id}
                    onContextMenu={(e) => handleAssetContextMenu(e, asset.id)}
                  >
                    <AssetCard
                      asset={asset}
                      selected={selectedIds.has(asset.id)}
                      onSelect={() =>
                        setSelectedIds((prev) => {
                          const next = new Set(prev);
                          if (next.has(asset.id)) next.delete(asset.id);
                          else next.add(asset.id);
                          return next;
                        })
                      }
                      onClick={() => openLightbox(asset, 'push')}
                      onToggleStar={handleToggleStar}
                    />
                  </div>
                ))}
              </div>
              
              {/* Loading more indicator */}
              {loadingMore && (
                <div className="flex items-center justify-center py-8">
                  <div className="w-8 h-8 border-4 border-indigo-500/20 border-t-indigo-500 rounded-full animate-spin"></div>
                  <span className="ml-3 text-slate-500 font-bold">載入更多...</span>
                </div>
              )}
              
              {/* No more data indicator */}
              {!hasMore && !loadingMore && assets.length > 0 && (
                <div className="flex items-center justify-center py-8">
                  <p className="text-slate-400 text-sm font-bold">已載入所有素材</p>
                </div>
              )}
            </>
          )}
        </div>

        {/* Selected Items Toolbar */}
        {selectedIds.size > 0 && (
          <div className="fixed bottom-10 left-1/2 -translate-x-1/2 z-40 animate-in slide-in-from-bottom-5">
            <div className="bg-slate-900 text-white px-6 py-4 rounded-2xl shadow-2xl flex items-center gap-6 border border-white/10 backdrop-blur-xl">
              <span className="font-black text-xs bg-indigo-600 px-3 py-1.5 rounded-lg">
                {selectedIds.size} 項目
              </span>
              <button
                onClick={() => handleBatchToggleStar(true)}
                className="flex items-center gap-2 px-4 py-2 bg-amber-500/20 hover:bg-amber-500 text-amber-300 hover:text-white rounded-xl transition-all font-black text-xs uppercase"
              >
                <Star size={14} /> 加星
              </button>
              <button
                onClick={() =>
                  openTagModal({
                    type: 'assets',
                    title: `已選 ${selectedIds.size} 個素材`,
                    assetIds: Array.from(selectedIds),
                  })
                }
                className="flex items-center gap-2 px-4 py-2 bg-indigo-500/20 hover:bg-indigo-500 text-indigo-300 hover:text-white rounded-xl transition-all font-black text-xs uppercase"
              >
                <Tags size={14} /> 標籤
              </button>
              <button
                onClick={() => handleBatchToggleStar(false)}
                className="flex items-center gap-2 px-4 py-2 bg-slate-700/60 hover:bg-slate-600 text-slate-200 rounded-xl transition-all font-black text-xs uppercase"
              >
                <Star size={14} /> 移星
              </button>
              <button
                onClick={handleBatchDelete}
                className="flex items-center gap-2 px-4 py-2 bg-red-500/20 hover:bg-red-500 text-red-400 hover:text-white rounded-xl transition-all font-black text-xs uppercase"
              >
                <Trash2 size={14} /> 刪除
              </button>
              <button
                onClick={() => setShowShareModal(true)}
                className="flex items-center gap-2 px-4 py-2 bg-indigo-500/20 hover:bg-indigo-500 text-indigo-300 hover:text-white rounded-xl transition-all font-black text-xs uppercase"
              >
                <Share2 size={14} /> 分享
              </button>
              <button
                onClick={() => setSelectedIds(new Set())}
                className="px-4 py-2 hover:bg-white/10 rounded-xl transition-all font-black text-xs uppercase text-slate-400"
              >
                取消
              </button>
            </div>
          </div>
        )}

        {activePanel === 'nas' && (
          <div className="fixed bottom-3 left-3 z-40 max-w-[min(72vw,680px)]">
            <div className="bg-slate-900 text-white border border-slate-700 rounded-lg px-3 py-2 shadow-2xl">
              <div className="flex items-center justify-between gap-3 mb-2">
                <div className="text-[10px] font-black text-slate-200">NAS 篩選資訊</div>
                <button
                  onClick={() => setIsBottomInfoCollapsed((prev) => !prev)}
                  className="text-[10px] font-bold text-slate-300 hover:text-white px-2 py-0.5 rounded border border-slate-600 hover:border-slate-500"
                >
                  {isBottomInfoCollapsed ? '展開' : '收合'}
                </button>
              </div>

              {!isBottomInfoCollapsed && (
                <>
                  <div className="flex flex-wrap items-center gap-3 text-[9px] text-slate-300 mb-2">
                <span className="flex items-center gap-1">
                  <span className="inline-block min-w-[18px] h-4 px-1 rounded bg-amber-200 text-amber-700 text-[8px] font-bold text-center leading-4">3</span>
                  當前目錄
                </span>
                <span className="flex items-center gap-1">
                  <span className="inline-block min-w-[18px] h-4 px-1 rounded bg-indigo-100 text-indigo-600 text-[8px] font-bold text-center leading-4">12</span>
                  含子目錄
                </span>
                <span className="flex items-center gap-1">
                  <EyeOff size={9} className="text-slate-300" />
                  已隱藏
                </span>
              </div>

              {selectedNasPath ? (
                <>
                  <div className="text-[10px] font-bold text-slate-300 mb-1">
                    當前目錄（{nasPathMode === 'exact' ? '僅當前目錄' : '含子孫目錄'}）
                  </div>
                  <div className="text-xs font-mono break-all leading-snug">{selectedNasPath}</div>
                </>
              ) : (
                <div className="text-[10px] font-bold text-slate-400">尚未選擇目錄</div>
              )}
                </>
              )}
            </div>
          </div>
        )}
      </main>

      {/* Modals and Overlays */}
      <ContextMenu
        state={contextMenu}
        onAction={executeContextAction}
        clipboardFolder={clipboardFolder}
        canEditSingleAsset={contextMenu.type === 'ASSET' && contextMenuAssetIds.length === 1}
        tagMenu={{
          enabled: contextMenu.type === 'ASSET' || contextMenu.type === 'NAS_FOLDER',
          addOptions: contextMenuAddTagOptions,
          addTypeOptions: contextMenuAddTagTypeOptions,
          removeOptions: contextMenuRemoveTagOptions,
          manageAction: contextMenu.type === 'NAS_FOLDER' ? 'manageNasTags' : 'manageTags',
        }}
      />
      {showUploadModal && (
        <UploadModal
          onClose={() => setShowUploadModal(false)}
          onBatchUpload={handleBatchUpload}
        />
      )}
      {selectedAsset && (
        <EditModal
          asset={selectedAsset}
          onClose={() => setSelectedAsset(null)}
          onUpdate={handleUpdate}
          onDelete={handleDelete}
          onToast={showToast}
        />
      )}
      {lightboxAsset && (
        <Lightbox
          asset={lightboxAsset}
          allAssets={lightboxAssets}
          onClose={closeLightbox}
          onNavigate={(asset) => openLightbox(asset)}
          onEdit={openEditModalWithLatest}
          onToggleStar={handleToggleStar}
        />
      )}
      {uploadTasks.length > 0 && (
        <UploadProgress
          tasks={uploadTasks}
          onClose={() => setUploadTasks([])}
        />
      )}
      {globalToast && (
        <div
          className={`fixed top-6 left-1/2 -translate-x-1/2 z-[260] text-sm font-black px-6 py-3 rounded-2xl shadow-2xl border animate-in fade-in slide-in-from-top-2 duration-200 ${
            globalToastTone === 'success'
              ? 'bg-emerald-600 text-white border-emerald-400/50'
              : globalToastTone === 'error'
                ? 'bg-rose-600 text-white border-rose-400/50'
                : 'bg-slate-900 text-white border-white/10'
          }`}
        >
          {globalToast}
        </div>
      )}
      {showTagModal && tagModalTarget && (
        <div className="fixed inset-0 z-[265] flex items-center justify-center">
          <button
            className="absolute inset-0 bg-black/35"
            onClick={() => setShowTagModal(false)}
            aria-label="close-tag-modal"
          />
          <div className="relative w-[760px] max-w-[95vw] max-h-[86vh] bg-white rounded-2xl border border-slate-200 shadow-2xl p-5 flex flex-col">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h3 className="text-base font-black text-slate-900">標籤管理</h3>
                <p className="text-xs text-slate-500 mt-0.5">{tagModalTarget.title}（共 {tagModalTarget.assetIds.length} 筆）</p>
              </div>
              <button
                onClick={() => setShowTagModal(false)}
                className="p-2 rounded-lg hover:bg-slate-100 text-slate-500"
              >
                <X size={16} />
              </button>
            </div>

            <div className="mb-3 flex items-center gap-2">
              <button
                onClick={() => {
                  setTagActionMode('add');
                  setSelectedTagKeys(new Set());
                }}
                className={`px-3 py-1.5 rounded-lg text-xs font-black ${tagActionMode === 'add' ? 'bg-indigo-600 text-white' : 'bg-slate-100 text-slate-600'}`}
              >
                新增標籤
              </button>
              <button
                onClick={() => {
                  setTagActionMode('remove');
                  setSelectedTagKeys(new Set());
                }}
                className={`px-3 py-1.5 rounded-lg text-xs font-black ${tagActionMode === 'remove' ? 'bg-rose-600 text-white' : 'bg-slate-100 text-slate-600'}`}
              >
                移除標籤
              </button>
            </div>

            {tagActionMode === 'add' && (
              <div className="mb-3 p-3 rounded-xl border border-slate-200 bg-slate-50">
                <p className="text-[11px] font-black text-slate-500 mb-2">建立新標籤（類型 / 名稱）</p>
                <div className="grid grid-cols-1 md:grid-cols-[1fr_2fr_auto] gap-2">
                  <input
                    value={newTagCategory}
                    onChange={(e) => setNewTagCategory(e.target.value)}
                    placeholder="類型"
                    className="px-3 py-2 text-xs bg-white border border-slate-200 rounded-lg outline-none focus:ring-2 focus:ring-indigo-500/20"
                  />
                  <input
                    value={newTagName}
                    onChange={(e) => setNewTagName(e.target.value)}
                    placeholder="名稱"
                    className="px-3 py-2 text-xs bg-white border border-slate-200 rounded-lg outline-none focus:ring-2 focus:ring-indigo-500/20"
                  />
                  <button
                    onClick={handleCreateTagDefinition}
                    className="px-3 py-2 text-xs font-black bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                  >
                    建立
                  </button>
                </div>
              </div>
            )}

            <div className="flex-1 overflow-y-auto border border-slate-200 rounded-xl p-2 space-y-1">
              {(tagActionMode === 'add' ? tagDefinitions : tagDefinitions.filter((tag) => removableTagKeys.has(tag.tagKey))).map((tag) => {
                const checked = selectedTagKeys.has(tag.tagKey);
                return (
                  <button
                    key={tag.tagKey}
                    onClick={() => {
                      setSelectedTagKeys((prev) => {
                        const next = new Set(prev);
                        if (next.has(tag.tagKey)) next.delete(tag.tagKey);
                        else next.add(tag.tagKey);
                        return next;
                      });
                    }}
                    className={`w-full text-left px-3 py-2 rounded-lg border transition-colors ${checked ? (tagActionMode === 'add' ? 'border-indigo-300 bg-indigo-50' : 'border-rose-300 bg-rose-50') : 'border-slate-200 hover:bg-slate-50'}`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-xs font-bold text-slate-800 truncate">{getTagLabel(tag)}</p>
                      <span className="text-[10px] text-slate-400 shrink-0">使用 {tag.usageCount || 0}</span>
                    </div>
                  </button>
                );
              })}
              {tagActionMode === 'remove' && removableTagKeys.size === 0 && (
                <p className="text-xs text-slate-400 text-center py-8">目前選取範圍沒有可移除的標籤</p>
              )}
            </div>

            <div className="mt-3 flex items-center justify-between">
              <p className="text-xs text-slate-500">已選擇 {selectedTagKeys.size} 個標籤</p>
              <button
                onClick={handleApplyTags}
                disabled={tagSubmitting || selectedTagKeys.size === 0}
                className={`px-4 py-2 rounded-lg text-xs font-black text-white disabled:opacity-40 ${tagActionMode === 'add' ? 'bg-indigo-600 hover:bg-indigo-700' : 'bg-rose-600 hover:bg-rose-700'}`}
              >
                {tagSubmitting ? '處理中...' : tagActionMode === 'add' ? '套用新增' : '套用移除'}
              </button>
            </div>
          </div>
        </div>
      )}
      {showShareModal && (
        <div className="fixed inset-0 z-[270] flex items-center justify-center">
          <button
            className="absolute inset-0 bg-black/35"
            onClick={() => setShowShareModal(false)}
            aria-label="close-share-modal"
          />
          <div className="relative w-[520px] max-w-[92vw] bg-white rounded-2xl border border-slate-200 shadow-2xl p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-black text-slate-900">分享清單</h3>
              <button
                onClick={() => setShowShareModal(false)}
                className="p-2 rounded-lg hover:bg-slate-100 text-slate-500"
              >
                <X size={16} />
              </button>
            </div>
            <p className="text-xs text-slate-500 mb-4">
              已選取 {selectedAssets.length} 項。每種清單都會以「一行一筆」複製到剪貼簿。
            </p>
            <div className="space-y-2">
              <button
                onClick={copyNasDirectoryList}
                className="w-full text-left px-3 py-2 rounded-lg border border-slate-200 hover:bg-slate-50"
              >
                <p className="text-sm font-bold text-slate-800">NAS目錄清單</p>
              </button>
              <button
                onClick={copyNasFileList}
                className="w-full text-left px-3 py-2 rounded-lg border border-slate-200 hover:bg-slate-50"
              >
                <p className="text-sm font-bold text-slate-800">NAS檔案清單</p>
              </button>
              <button
                onClick={copyThumbnailUrls}
                className="w-full text-left px-3 py-2 rounded-lg border border-slate-200 hover:bg-slate-50"
              >
                <p className="text-sm font-bold text-slate-800">縮圖網址</p>
              </button>
              <button
                onClick={copyHdUrls}
                disabled={sharingHdLinks}
                className="w-full text-left px-3 py-2 rounded-lg border border-slate-200 hover:bg-slate-50 disabled:opacity-50"
              >
                <p className="text-sm font-bold text-slate-800">高清網址</p>
                {sharingHdLinks && (
                  <p className="text-[11px] text-slate-500 mt-1">NAS 產製 HD 中，請稍候...</p>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default App;
